/**
 * ShopifyCallback — landing route for the Shopify OAuth redirect
 * (`/auth/shopify/callback`). Shopify sends the merchant back here with
 * `?code=&shop=&hmac=&state=` after they approve the app.
 *
 * We POST those params to /api/auth/shopify (which mirrors /api/auth/google):
 * the backend verifies hmac + state, exchanges the code for an offline token,
 * find-or-creates the user/workspace, and returns `{access_token, token_type,
 * user}`. We persist that via AuthContext.shopifyLogin and redirect.
 *
 * On error we show a friendly retry message that links back to /login.
 *
 * Only meaningful on the Shopify build, but the route is harmless on the
 * Stripe build (it just shows the error state since the backend rejects it /
 * the URL is never produced there).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { redirectAfterAuth } from '../utils/returnTo';

const ShopifyCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { shopifyLogin } = useAuth();
    const [error, setError] = useState('');
    // StrictMode double-mounts effects in dev — guard so we only POST once.
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;

        // Capture EVERY param Shopify returned (already URL-decoded). Shopify
        // signs the full set — host, timestamp, etc. — so the backend rebuilds
        // the HMAC over all params except redirect_uri/hmac. Forward them all
        // or the reconstructed signature won't match → 401.
        const params = Object.fromEntries(searchParams.entries());

        const { code = '', shop = '', hmac = '', state = '' } = params;

        if (!code || !shop || !hmac || !state) {
            setError('This sign-in link is missing required information. Please try again.');
            return;
        }

        const redirectUri = `${window.location.origin}/auth/shopify/callback`;

        authAPI
            .shopifyAuth({ ...params, code, shop, hmac, state, redirect_uri: redirectUri })
            .then((res) => {
                shopifyLogin(res.data);
                redirectAfterAuth(navigate);
            })
            .catch((err: any) => {
                setError(
                    err?.response?.data?.detail ||
                    'We could not complete your Shopify sign-in. Please try again.'
                );
            });
    }, [searchParams, navigate, shopifyLogin]);

    return (
        <div className="min-h-screen relative flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-900/10 to-transparent pointer-events-none"></div>
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[100px] animate-pulse-glow pointer-events-none" />

            <div className="max-w-md w-full space-y-6 relative z-10 glass p-10 rounded-3xl shadow-2xl text-center">
                <Link to="/" className="inline-flex items-center space-x-2 mb-2 group">
                    <img src="/logo-purple.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform dark:hidden" />
                    <img src="/logo-white.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform hidden dark:block" />
                </Link>

                {error ? (
                    <>
                        <div className="flex justify-center">
                            <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sign-in failed</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
                        <Link
                            to="/login"
                            className="inline-flex w-full btn-primary justify-center py-3"
                        >
                            Back to sign in
                        </Link>
                    </>
                ) : (
                    <>
                        <div className="flex justify-center">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Finishing sign-in…</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Connecting your Shopify store. This only takes a moment.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShopifyCallback;
